import { invokeWithRetry } from "@/services/edgeFunctionWrappers/common/invokeWithRetry";
import i18n from "@/i18n";

/**
 * Échéanciers d'abonnement (`subscription_payment_schedules`).
 *
 * La fonction edge `subscription-payment-schedules` existait déjà mais n'était
 * appelée de nulle part : l'écran d'échelonnement se contentait d'un
 * `setTimeout` suivi d'un toast de succès. Rien n'était enregistré.
 */

export interface PaymentScheduleInput {
  installmentNumber: number;
  /** Date d'échéance au format `yyyy-mm-dd` (colonne `due_date`, type date). */
  dueDate: string;
  amount: number;
}

export interface PaymentScheduleRow {
  id: string;
  subscription_id: string;
  installment_number: number;
  due_date: string;
  amount: number;
  status: string;
  payment_id?: string | null;
}

export interface ServiceResult<T> {
  data: T | null;
  error: Error | null;
}

/**
 * Le contrat de `invokeWithRetry` est en cours de reprise : selon la version,
 * une défaillance ressort par une levée, par `error`, ou seulement par
 * `authExpired` (voire par un HTTP 200 portant `{ error }`). On couvre les
 * quatre sorties pour ne jamais annoncer un échéancier enregistré à tort.
 */
async function invokeSchedules<T>(body: Record<string, unknown>): Promise<ServiceResult<T>> {
  try {
    const result = await invokeWithRetry<T & { error?: unknown }>(
      "subscription-payment-schedules",
      body
    );

    if (result?.authExpired) {
      return { data: null, error: new Error(i18n.t("common:errors.sessionExpired")) };
    }
    if (result?.error) {
      return { data: null, error: result.error as Error };
    }
    // La fonction renvoie ses erreurs métier en HTTP 400 avec `{ error }`,
    // mais un 200 porteur d'un champ `error` doit aussi être traité en échec.
    if (result?.data?.error) {
      return { data: null, error: new Error(String(result.data.error)) };
    }

    return { data: result?.data as T, error: null };
  } catch (err: unknown) {
    return { data: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}

/**
 * Crée l'échéancier. Ne renvoie un succès que si le serveur a bien retourné
 * autant de lignes que d'échéances demandées.
 */
export async function createPaymentSchedule(
  subscriptionId: string,
  paymentSchedule: PaymentScheduleInput[]
): Promise<ServiceResult<PaymentScheduleRow[]>> {
  if (!subscriptionId) {
    return { data: null, error: new Error(i18n.t("common:errors.noSubscriptionSelected")) };
  }
  if (!paymentSchedule.length) {
    return { data: null, error: new Error(i18n.t("common:errors.emptySchedule")) };
  }

  const { data, error } = await invokeSchedules<{ success?: boolean; schedules?: PaymentScheduleRow[] }>({
    action: "create_payment_schedule",
    subscriptionId,
    paymentSchedule,
  });

  if (error) return { data: null, error };

  const schedules = data?.schedules ?? [];
  if (!data?.success || schedules.length !== paymentSchedule.length) {
    return {
      data: null,
      error: new Error(
        i18n.t("common:errors.incompleteSchedule", {
          count: schedules.length,
          total: paymentSchedule.length,
        })
      ),
    };
  }

  return { data: schedules, error: null };
}

/** Échéancier existant d'un abonnement. */
export async function getPaymentSchedules(
  subscriptionId: string
): Promise<ServiceResult<PaymentScheduleRow[]>> {
  const { data, error } = await invokeSchedules<{ schedules?: PaymentScheduleRow[] }>({
    action: "get_payment_schedules",
    subscriptionId,
  });

  if (error) return { data: null, error };
  return { data: data?.schedules ?? [], error: null };
}
